import React, { useEffect } from "react";
import { MapLocation } from "../utils/MarkerClusterManager";
import BaseSidebar from "./BaseSidebar";
import RestaurantDetailsContent from "./RestaurantDetailsContent";
import { SidebarType } from "../utils/SidebarStateManager";
import { structuredDataManager, StructuredDataManager } from "../utils/StructuredDataManager";
import { generateRestoSlug } from "../utils/RestoSlugUtils";
// Breadcrumb and breadcrumbManager imports removed

interface RestaurantDetailsSidebarProps {
  selectedEntry: MapLocation["entry"] | null;
  selectedRestaurantId?: string | number | null;
  initialReports?: any[];
  initialChain?: any;
}

const RestaurantDetailsSidebar: React.FC<RestaurantDetailsSidebarProps> = ({
  selectedEntry,
  selectedRestaurantId = null,
  initialReports,
  initialChain
}) => {
  // Add structured data for restaurant when selected
  useEffect(() => {
    if (selectedEntry) {
      // Add restaurant structured data
      const restaurantSchema = StructuredDataManager.generateRestaurantSchema(selectedEntry);
      structuredDataManager.addStructuredData('restaurant', restaurantSchema);

      // Update page title and meta description for SEO
      const city = selectedEntry.address ? selectedEntry.address.split(',')[1]?.trim() : '';
      const titleSuffix = city ? ` in ${city}` : '';
      document.title = `${selectedEntry.name}${titleSuffix} - Food Safety Rating | Kauwen of Spauwen`;
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content',
          `Food safety information for ${selectedEntry.name}${titleSuffix}. View hygiene inspection results, ratings, and user reports. Current inspection status and history available.`
        );
      }

      // Update canonical URL with SEO-friendly slug
      const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (canonical) {
        const restoSlug = generateRestoSlug(selectedEntry.name, selectedEntry.id);
        canonical.href = `${window.location.origin}${window.location.pathname}?restoId=${restoSlug}`;
      }
    }

    return () => {
      // Clean up structured data when component unmounts
      structuredDataManager.removeStructuredData('restaurant');
      
      // Reset title and meta description
      document.title = 'Kauwen of Spauwen - Restaurant Food Safety Ratings Belgium';
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content',
          'Find restaurant food safety ratings and hygiene inspection results in Belgium. Report food poisoning experiences and discover safe dining options.'
        );
      }
    };
  }, [selectedEntry]);

  return (
    <BaseSidebar
      sidebarType={SidebarType.RESTAURANT_DETAILS}
    >
      <RestaurantDetailsContent
        selectedEntry={selectedEntry}
        selectedRestaurantId={selectedRestaurantId}
        initialChain={initialChain}
        initialReports={initialReports}
      />
    </BaseSidebar>
  );
};

export default RestaurantDetailsSidebar;
